/**
 * CompliancePolicies.tsx — Policy templates page (Layer 4).
 *
 * Lists policy templates per framework. Users can review and digitally sign
 * policies. Signed policies show the signer name and date.
 */

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from '../config/api';
import { useAuth } from '../context/AuthContext';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import ArticleIcon from '@mui/icons-material/Article';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DrawIcon from '@mui/icons-material/Draw';
import GavelIcon from '@mui/icons-material/Gavel';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Framework {
  id: string;
  name: string;
}

interface Policy {
  id: string;
  framework_id: string;
  policy_name: string;
  policy_template: string;
  version: string;
  organization_id: string | null;
  signed_by: string | null;
  signed_at: string | null;
  active: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CompliancePolicies() {
  const { user } = useAuth();
  const orgId     = user?.customer_id;

  const [frameworks,       setFrameworks]       = useState<Framework[]>([]);
  const [selectedFw,       setSelectedFw]       = useState('');
  const [policies,         setPolicies]         = useState<Policy[]>([]);
  const [loading,          setLoading]          = useState(false);
  const [fwLoading,        setFwLoading]        = useState(true);

  // Review dialog
  const [reviewPolicy,     setReviewPolicy]     = useState<Policy | null>(null);
  const [signerName,       setSignerName]       = useState(user?.full_name ?? '');
  const [signing,          setSigning]          = useState(false);
  const [toast,            setToast]            = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Load frameworks
  useEffect(() => {
    (async () => {
      setFwLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/v1/compliance/frameworks`);
        setFrameworks(res.data);
        if (res.data.length > 0) setSelectedFw(res.data[0].name);
      } catch {
        setToast({ msg: 'Failed to load frameworks', severity: 'error' });
      } finally {
        setFwLoading(false);
      }
    })();
  }, []);

  // Load policies when framework changes
  const loadPolicies = useCallback(async (fwName: string) => {
    if (!fwName) return;
    setLoading(true);
    try {
      const params = orgId ? `?org_id=${orgId}` : '';
      const res = await axios.get(
        `${API_BASE}/v1/compliance/policies/${fwName}${params}`
      );
      setPolicies(res.data);
    } catch (err: any) {
      setToast({ msg: err?.response?.data?.detail || 'Failed to load policies', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (selectedFw) loadPolicies(selectedFw);
  }, [selectedFw, loadPolicies]);

  async function signPolicy(policy: Policy) {
    if (!orgId) {
      setToast({ msg: 'No organisation linked to your account', severity: 'error' });
      return;
    }
    if (!signerName.trim()) {
      setToast({ msg: 'Please enter a signer name', severity: 'error' });
      return;
    }
    setSigning(true);
    try {
      await axios.post(`${API_BASE}/v1/compliance/policies/${orgId}`, {
        framework_id: policy.framework_id,
        policy_name: policy.policy_name,
        policy_template: policy.policy_template,
        version: policy.version,
        signed_by: signerName.trim(),
      });
      setToast({ msg: `Policy "${policy.policy_name}" signed successfully`, severity: 'success' });
      setReviewPolicy(null);
      await loadPolicies(selectedFw);
    } catch (err: any) {
      setToast({ msg: err?.response?.data?.detail || 'Failed to sign policy', severity: 'error' });
    } finally {
      setSigning(false);
    }
  }

  if (fwLoading) {
    return (
      <Box>
        <Skeleton height={40} width={300} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <GavelIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Policy Templates</Typography>
          <Typography variant="body2" color="text.secondary">
            Review, customise, and digitally sign compliance policy templates
          </Typography>
        </Box>
      </Box>

      {/* Framework selector */}
      <FormControl size="small" sx={{ minWidth: 200, mb: 3 }}>
        <InputLabel>Framework</InputLabel>
        <Select
          value={selectedFw}
          label="Framework"
          onChange={(e) => setSelectedFw(e.target.value)}
        >
          {frameworks.map((fw) => (
            <MenuItem key={fw.id} value={fw.name}>{fw.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {!orgId && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Link your account to an organisation to sign policies.
        </Alert>
      )}

      {loading ? (
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : policies.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.disabled' }}>
          <ArticleIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography>No policy templates found for {selectedFw}</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {policies.map((policy) => {
            const isSigned = !!policy.signed_by;

            return (
              <Grid item xs={12} md={6} key={policy.id}>
                <Card
                  elevation={2}
                  sx={{
                    borderLeft: 4,
                    borderColor: isSigned ? '#2ea55e' : 'primary.main',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 6 },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {policy.policy_name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip label={`v${policy.version}`} size="small" variant="outlined" />
                        {isSigned && (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Signed"
                            size="small"
                            color="success"
                          />
                        )}
                      </Box>
                    </Box>

                    {isSigned ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleIcon sx={{ color: '#2ea55e', fontSize: 16 }} />
                        <Typography variant="body2" color="text.secondary">
                          Signed by <strong>{policy.signed_by}</strong> on{' '}
                          {new Date(policy.signed_at!).toLocaleDateString()}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Global template — not yet signed for your organisation
                      </Typography>
                    )}

                    {/* Policy preview */}
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1.5,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        maxHeight: 80,
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                        {policy.policy_template.slice(0, 250)}…
                      </Typography>
                      {/* Fade overlay */}
                      <Box sx={{
                        position: 'absolute', bottom: 0, left: 0, right: 0, height: 24,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.05))',
                      }} />
                    </Box>
                  </CardContent>

                  <Divider />

                  <CardActions sx={{ px: 2 }}>
                    <Button
                      size="small"
                      startIcon={<DrawIcon />}
                      variant={isSigned ? 'outlined' : 'contained'}
                      disabled={!orgId}
                      onClick={() => {
                        setReviewPolicy(policy);
                        setSignerName(user?.full_name ?? '');
                      }}
                    >
                      {isSigned ? 'Re-sign' : 'Review & Sign'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Review & Sign dialog */}
      {reviewPolicy && (
        <Dialog
          open
          onClose={() => setReviewPolicy(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {reviewPolicy.policy_name}
            <Chip label={`v${reviewPolicy.version}`} size="small" variant="outlined" sx={{ ml: 1 }} />
          </DialogTitle>
          <DialogContent dividers>
            <Box
              sx={{
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 1,
                maxHeight: 400,
                overflow: 'auto',
                mb: 3,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
              >
                {reviewPolicy.policy_template}
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" gutterBottom>Digital Signature</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              By entering your name and clicking "Sign Policy", you confirm that you have read,
              understand, and accept this policy on behalf of your organisation.
            </Typography>

            <TextField
              label="Full Name (Signer)"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              fullWidth
              size="small"
              helperText="This name will be recorded as the digital signature"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setReviewPolicy(null)} disabled={signing}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={signing ? <CircularProgress size={14} color="inherit" /> : <DrawIcon />}
              disabled={signing || !signerName.trim()}
              onClick={() => signPolicy(reviewPolicy)}
            >
              {signing ? 'Signing…' : 'Sign Policy'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Toast */}
      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
